import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSortBy, useTable } from "react-table";
import currency from "currency.js";
import { ethers } from "ethers";
import Select from "antd/es/select";
import { DataContext } from "../../context/DataContext";
import {
  useStakeInfo,
  getCurrentEpoch,
} from "../../hooks/api/staking/useStakeInfo";
import { useStakedAccountTrades } from "../../hooks/api/staking/useStakedAccountTrades";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import {
  Align,
  CopyButton,
  DashboardRow,
  TableHeaderCell,
  TableWrapper,
  TitleWrapper,
} from "../Table/style";
import { ITableColumn } from "../Table/TableColumn";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import { PageEndpointEnum } from "../../enums/endpoints";
import { Spinner } from "../shared/Spinner";
import useScreenSize from "../../hooks/api/screenSize/useScreenSize";
import { MobileDirectory } from "../shared/MobileDirectory";
import { SPACING } from "../../constants/design/spacing";
import { Tab, TabsWrapper } from "./style";
import { TradingRewardsInfo } from "./TradingRewardsInfo";
import { MegaRewards } from "./MegaRewards";
import { BuybacksAndBurns } from "./BuybacksAndBurns";
import { WeeklyRewardAllocations } from "../WeeklyRewardAllocations";
import {
  calculateStakerRewardsMetrics,
  IStakerRewardsMetrics,
} from "../../utils/staking";
import { shortenAddress } from "../../utils/strings";
import { useStoreEpochStake } from "../../hooks/api/staking/useStoreEpochStake";
import { useEpochRewards } from "../../hooks/api/staking/useEpochRewards";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import ConfirmationModal from "../shared/ConfirmationModal";
import { FONT_STYLE } from "../../constants/design/fontSize";

enum StakeTabEnum {
  WEEKLY_ALLOCATION = "weekly-allocation",
  STAKING = "staking",
  TRADING_REWARDS_INFO = "trading-rewards-info",
  MEGA_REWARDS = "mega-rewards",
  BUYBACKS_AND_BURNS = "buybacks-and-burns",
}

// Total rewards per epoch (in token units, e.g., AEVO tokens)
const TOTAL_REWARDS_PER_EPOCH = 250000;

export function Stake() {
  const { records } = useContext(DataContext);
  const [activeTab, setActiveTab] = useState<StakeTabEnum>(
    StakeTabEnum.WEEKLY_ALLOCATION
  );
  const currentEpoch = getCurrentEpoch();
  const [selectedEpoch, setSelectedEpoch] = useState<number | undefined>(
    currentEpoch
  );
  const [isStoring, setIsStoring] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmedOverwrite, setConfirmedOverwrite] = useState(false);
  const { data: stakeInfos, isLoading: isStakeLoading } =
    useStakeInfo(selectedEpoch);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isMobileScreen } = useScreenSize();
  const { storeEpochStake } = useStoreEpochStake();
  const { data: epochRewards } = useEpochRewards();

  // Get account addresses from stakeInfos
  const stakedAccountAddresses = useMemo(
    () => (stakeInfos || []).map((info) => info.accountAddress),
    [stakeInfos]
  );

  // Fetch volume data for staked accounts (for the selected epoch)
  const { data: volumeData, isLoading: isVolumeLoading } =
    useStakedAccountTrades(stakedAccountAddresses, selectedEpoch);

  // Create a map of account -> volume for quick lookup
  const volumeMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (volumeData) {
      volumeData.forEach((item) => {
        const normalizedAccount = item.Account.toLowerCase();
        map[normalizedAccount] = Number(item.TotalVolume);
        map[item.Account] = Number(item.TotalVolume);
      });
    }
    return map;
  }, [volumeData]);

  // Check if rewards already exist for the selected epoch
  const hasExistingRewardsForSelectedEpoch = useMemo(() => {
    if (!selectedEpoch) return false;
    if (!epochRewards) {
      // eslint-disable-next-line no-console
      console.log(
        `Checking rewards for epoch ${selectedEpoch}: epochRewards is ${epochRewards}`
      );
      return false;
    }
    // Ensure we compare numbers (epoch might be string or number)
    const selectedEpochNum = Number(selectedEpoch);
    const exists = epochRewards.some(
      (reward) => Number(reward.Epoch) === selectedEpochNum
    );
    // eslint-disable-next-line no-console
    console.log(
      `Checking rewards for epoch ${selectedEpoch}:`,
      `epochRewards length=${epochRewards.length}`,
      `exists=${exists}`,
      "epochs in rewards:",
      epochRewards.map((r) => r.Epoch),
      `selectedEpoch type: ${typeof selectedEpoch}, value: ${selectedEpoch}`,
      `hasExistingRewardsForSelectedEpoch=${exists}`
    );
    return exists;
  }, [selectedEpoch, epochRewards]);

  // Check if the selected epoch hasn't ended yet (can only store after epoch has ended)
  // Selected epoch can only be stored if currentEpoch > selectedEpoch
  const isSelectedEpochActive = useMemo(() => {
    if (!selectedEpoch) return false;
    // Can only store if current epoch is greater than selected epoch
    // (meaning the selected epoch has ended)
    return currentEpoch <= selectedEpoch;
  }, [selectedEpoch, currentEpoch]);

  // Calculate metrics for each staker
  const rewardsMetrics = useMemo(() => {
    if (!stakeInfos) return [];

    // First pass: calculate base metrics and stake values
    const metricsWithStakeValue = stakeInfos.map((stakeInfo) => {
      // Checksum the address
      let checksummedAddress = stakeInfo.accountAddress;
      try {
        checksummedAddress = ethers.utils.getAddress(stakeInfo.accountAddress);
      } catch {
        // If address is invalid, use as-is
      }

      const normalizedAddress = checksummedAddress.toLowerCase();
      const weeklyVolume =
        volumeMap[normalizedAddress] ||
        volumeMap[checksummedAddress] ||
        volumeMap[stakeInfo.accountAddress] ||
        0;

      const baseMetrics = calculateStakerRewardsMetrics(
        checksummedAddress,
        stakeInfo.stakeAmount,
        stakeInfo.stakeDuration,
        weeklyVolume
      );

      // Calculate stake value: stakeAmount (in wei) × combinedMultiplier
      const stakeAmountWei = Number(stakeInfo.stakeAmount);
      const stakeValue = stakeAmountWei * baseMetrics.combinedMultiplier;

      return {
        ...baseMetrics,
        stakeValue,
      };
    });

    // Calculate total stake value
    const totalStakeValue = metricsWithStakeValue.reduce(
      (sum, metric) => sum + metric.stakeValue,
      0
    );

    // Second pass: calculate percentage share and reward amount
    return metricsWithStakeValue.map((metric) => {
      const percentageShare =
        totalStakeValue > 0 ? metric.stakeValue / totalStakeValue : 0;
      const rewardAmount = percentageShare * TOTAL_REWARDS_PER_EPOCH;

      return {
        ...metric,
        percentageShare,
        rewardAmount,
      };
    });
  }, [stakeInfos, volumeMap]);

  const isLoading = isStakeLoading || isVolumeLoading;

  // Calculate total staked from all accounts
  const totalStaked = useMemo(() => {
    if (!rewardsMetrics || rewardsMetrics.length === 0) return 0;
    return rewardsMetrics.reduce((sum, metric) => {
      const stakeAmountWei = Number(metric.stakeAmount);
      return sum + stakeAmountWei;
    }, 0);
  }, [rewardsMetrics]);

  const onCopy = useCallback(
    (address: string) => {
      navigator.clipboard.writeText(address);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Address Copied",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  // Convert address to checksum format for display
  const getChecksumAddress = useCallback((address: string) => {
    try {
      return ethers.utils.getAddress(address);
    } catch {
      return address; // Return as-is if not a valid address
    }
  }, []);

  const getAirtableTag = useCallback(
    (address: string) => {
      // Check both checksummed and lowercase versions
      const checksummed = getChecksumAddress(address);
      const lowercased = address.toLowerCase();
      const rawTag =
        records[checksummed] ||
        records[lowercased] ||
        records[address] ||
        undefined;

      // If the tag contains the full address, extract just the nickname part
      let finalTag = rawTag;
      if (rawTag && rawTag.includes(checksummed)) {
        const parts = rawTag.split(" - ");
        if (parts.length > 1) {
          // Return everything after the address
          finalTag = parts.slice(1).join(" - ");
        }
      }

      return finalTag;
    },
    [records, getChecksumAddress]
  );

  // Format stake duration from seconds to readable format
  const formatStakeDuration = useCallback((seconds: string) => {
    const duration = Number(seconds);

    // Map specific durations to months
    const durationMap: Record<number, string> = {
      4838400: "2 months",
      13305600: "5 months",
      21772800: "8 months",
      30844800: "12 months",
    };

    if (durationMap[duration]) {
      return durationMap[duration];
    }

    // Fallback: calculate months from seconds (30 days per month)
    const months = Math.round(duration / (30 * 24 * 60 * 60));
    if (months > 0) {
      return `${months} month${months !== 1 ? "s" : ""}`;
    }

    // Fallback to days/hours if less than a month
    const days = Math.floor(duration / 86400);
    const hours = Math.floor((duration % 86400) / 3600);
    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    return `${hours}h`;
  }, []);

  // Format stake amount from wei
  const formatStakeAmount = useCallback((amount: string) => {
    // Assuming 18 decimals
    const amountInEth = Number(amount) / 1e18;
    return currency(amountInEth, { precision: 2 }).format({ symbol: "" });
  }, []);

  // Format volume
  const formatVolume = useCallback((volume: number | undefined) => {
    if (!volume) return "-";
    return currency(volume, { precision: 2 }).format({ symbol: "$" });
  }, []);

  // Format percentage
  const formatPercentage = useCallback(
    (percentage: number) => `${(percentage * 100).toFixed(4)}%`,
    []
  );

  // Format reward amount
  const formatRewardAmount = useCallback(
    (amount: number) =>
      currency(amount, { precision: 0 }).format({ symbol: "" }),
    []
  );

  // Store the selected epoch
  const handleStoreEpochStake = useCallback(async () => {
    if (!rewardsMetrics || rewardsMetrics.length === 0 || !selectedEpoch) {
      return;
    }

    setIsStoring(true);
    await storeEpochStake(selectedEpoch, rewardsMetrics);
    setIsStoring(false);
    setShowConfirmModal(false);
    setConfirmedOverwrite(false);
  }, [selectedEpoch, rewardsMetrics, storeEpochStake]);

  const handleStoreEpochStakeClick = useCallback(() => {
    if (!rewardsMetrics || rewardsMetrics.length === 0) {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Cannot Store",
          subheader: "No rewards data available to store",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
      return;
    }

    if (!selectedEpoch) {
      return;
    }

    // If selected epoch is still active, don't allow storing
    if (isSelectedEpochActive) {
      return;
    }

    // If rewards already exist for selected epoch, don't allow storing (button should be disabled)
    if (hasExistingRewardsForSelectedEpoch) {
      return;
    }

    // Otherwise, proceed directly
    handleStoreEpochStake();
  }, [
    rewardsMetrics,
    selectedEpoch,
    hasExistingRewardsForSelectedEpoch,
    isSelectedEpochActive,
    addToast,
    handleStoreEpochStake,
  ]);

  const columns: ITableColumn<IStakerRewardsMetrics>[] = useMemo(
    () => [
      {
        id: "account",
        title: "Address",
        align: "left",
        accessor: "accountAddress",
        Cell: ({ row }: any) => {
          const checksummedAddress = getChecksumAddress(
            row.original.accountAddress
          );
          const airtableTag = getAirtableTag(row.original.accountAddress);
          const shortenedAddress = shortenAddress(checksummedAddress);
          const displayText = airtableTag
            ? `${shortenedAddress} - ${airtableTag}`
            : shortenedAddress;
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <CopyButton
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy(checksummedAddress);
                }}
              >
                <Copy />
              </CopyButton>
              <div
                style={{
                  color: airtableTag ? COLORS.blue.one : TEXT_COLORS.one,
                  marginLeft: SPACING.two,
                }}
              >
                {displayText}
              </div>
            </div>
          );
        },
      },
      {
        title: "Staked",
        align: "right",
        accessor: "stakeAmount",
        Cell: ({ value }: any) => (
          <Align align="right">{formatStakeAmount(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const amountA = Number(a.original.stakeAmount);
          const amountB = Number(b.original.stakeAmount);
          if (amountA > amountB) return 1;
          if (amountA < amountB) return -1;
          return 0;
        },
      },
      {
        title: "Stake Duration",
        align: "right",
        accessor: "accountAddress",
        Cell: ({ row }: any) => {
          // Find the original stake info to get duration
          const stakeInfo = stakeInfos?.find(
            (info) =>
              info.accountAddress.toLowerCase() ===
              row.original.accountAddress.toLowerCase()
          );
          if (!stakeInfo) return <Align align="right">-</Align>;
          return (
            <Align align="right">
              {formatStakeDuration(stakeInfo.stakeDuration)}
            </Align>
          );
        },
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const stakeInfoA = stakeInfos?.find(
            (info) =>
              info.accountAddress.toLowerCase() ===
              a.original.accountAddress.toLowerCase()
          );
          const stakeInfoB = stakeInfos?.find(
            (info) =>
              info.accountAddress.toLowerCase() ===
              b.original.accountAddress.toLowerCase()
          );
          const durationA = stakeInfoA ? Number(stakeInfoA.stakeDuration) : 0;
          const durationB = stakeInfoB ? Number(stakeInfoB.stakeDuration) : 0;
          if (durationA > durationB) return 1;
          if (durationA < durationB) return -1;
          return 0;
        },
      },
      {
        title: "Vesting",
        align: "right",
        accessor: "vestingPeriod",
        Cell: ({ value }: any) => (
          <Align align="right">
            {value} week{value !== 1 ? "s" : ""}
          </Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const vestingA = Number(a.original.vestingPeriod);
          const vestingB = Number(b.original.vestingPeriod);
          if (vestingA > vestingB) return 1;
          if (vestingA < vestingB) return -1;
          return 0;
        },
      },
      {
        title: "Volume",
        align: "right",
        accessor: "tradeVolume",
        Cell: ({ value }: any) => (
          <Align align="right">{formatVolume(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const volumeA = Number(a.original.tradeVolume);
          const volumeB = Number(b.original.tradeVolume);
          if (volumeA > volumeB) return 1;
          if (volumeA < volumeB) return -1;
          return 0;
        },
      },
      {
        title: "Total Multiplier",
        align: "right",
        accessor: "combinedMultiplier",
        Cell: ({ value }: any) => (
          <Align align="right">{value.toFixed(2)}x</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const multiplierA = Number(a.original.combinedMultiplier);
          const multiplierB = Number(b.original.combinedMultiplier);
          if (multiplierA > multiplierB) return 1;
          if (multiplierA < multiplierB) return -1;
          return 0;
        },
      },
      {
        title: "% Share",
        align: "right",
        accessor: "percentageShare",
        Cell: ({ value }: any) => (
          <Align align="right">{formatPercentage(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const shareA = Number(a.original.percentageShare);
          const shareB = Number(b.original.percentageShare);
          if (shareA > shareB) return 1;
          if (shareA < shareB) return -1;
          return 0;
        },
      },
      {
        title: "Rewards",
        align: "right",
        accessor: "rewardAmount",
        Cell: ({ value }: any) => (
          <Align align="right">{formatRewardAmount(value)} AEVO</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const rewardA = Number(a.original.rewardAmount);
          const rewardB = Number(b.original.rewardAmount);
          if (rewardA > rewardB) return 1;
          if (rewardA < rewardB) return -1;
          return 0;
        },
      },
    ],
    [
      onCopy,
      formatStakeAmount,
      formatStakeDuration,
      formatVolume,
      formatPercentage,
      formatRewardAmount,
      getChecksumAddress,
      getAirtableTag,
      stakeInfos,
    ]
  );

  const memoizedRewardsMetrics = useMemo(
    () => rewardsMetrics || [],
    [rewardsMetrics]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: memoizedRewardsMetrics,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        initialState: {
          sortBy: [
            {
              id: "rewardAmount",
              desc: true,
            },
          ],
        },
        autoResetSortBy: false,
      },
      useSortBy
    );

  // Generate valid epochs: 1 to currentEpoch
  const validEpochs = useMemo(
    () => Array.from({ length: currentEpoch }, (_, i) => i + 1).reverse(),
    [currentEpoch]
  );

  return (
    <div style={{ padding: isMobileScreen ? SPACING.two : SPACING.four }}>
      {isMobileScreen ? (
        <div style={{ marginBottom: SPACING.two }}>
          <MobileDirectory />
        </div>
      ) : null}
      <TabsWrapper>
        <Tab
          isActive={activeTab === StakeTabEnum.WEEKLY_ALLOCATION}
          onClick={() => setActiveTab(StakeTabEnum.WEEKLY_ALLOCATION)}
        >
          Weekly Allocation
        </Tab>
        <Tab
          isActive={activeTab === StakeTabEnum.STAKING}
          onClick={() => setActiveTab(StakeTabEnum.STAKING)}
        >
          Staking
        </Tab>
        <Tab
          isActive={activeTab === StakeTabEnum.TRADING_REWARDS_INFO}
          onClick={() => setActiveTab(StakeTabEnum.TRADING_REWARDS_INFO)}
        >
          Trading
        </Tab>
        <Tab
          isActive={activeTab === StakeTabEnum.MEGA_REWARDS}
          onClick={() => setActiveTab(StakeTabEnum.MEGA_REWARDS)}
        >
          Mega Rewards
        </Tab>
        <Tab
          isActive={activeTab === StakeTabEnum.BUYBACKS_AND_BURNS}
          onClick={() => setActiveTab(StakeTabEnum.BUYBACKS_AND_BURNS)}
        >
          Buybacks and Burns
        </Tab>
      </TabsWrapper>

      {activeTab === StakeTabEnum.WEEKLY_ALLOCATION && (
        <WeeklyRewardAllocations />
      )}

      {activeTab === StakeTabEnum.STAKING && (
        <>
          <TitleWrapper>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <h3>
                Total Stakers{" "}
                {memoizedRewardsMetrics.length
                  ? `(${memoizedRewardsMetrics.length})`
                  : "(None)"}
              </h3>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: SPACING.two,
                }}
              >
                <span style={{ marginRight: SPACING.one }}>Epoch:</span>
                <Select
                  value={selectedEpoch ?? null}
                  onChange={(value) => setSelectedEpoch(value ?? undefined)}
                  style={{ width: "120px" }}
                  placeholder="Select Epoch"
                  allowClear
                >
                  {validEpochs.map((epoch) => (
                    <Select.Option key={epoch} value={epoch}>
                      {epoch}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </TitleWrapper>

          {selectedEpoch && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: SPACING.two,
                marginBottom: SPACING.three,
              }}
            >
              {memoizedRewardsMetrics.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: SPACING.four,
                    color: TEXT_COLORS.one,
                  }}
                >
                  <div>
                    <strong>Total Staked:</strong>{" "}
                    {formatStakeAmount(String(totalStaked))} AEVO
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: SPACING.two,
                }}
              >
                {isSelectedEpochActive && selectedEpoch && (
                  <span style={{ color: COLORS.orange.one, fontSize: "14px" }}>
                    Epoch {selectedEpoch} hasn&apos;t ended yet
                  </span>
                )}
                {!isSelectedEpochActive &&
                  hasExistingRewardsForSelectedEpoch &&
                  selectedEpoch && (
                    <span
                      style={{ color: COLORS.orange.one, fontSize: "14px" }}
                    >
                      Rewards for epoch {selectedEpoch} are already stored
                    </span>
                  )}
                <Button
                  buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                  onClick={handleStoreEpochStakeClick}
                  disabled={
                    isStoring ||
                    isLoading ||
                    memoizedRewardsMetrics.length === 0 ||
                    isSelectedEpochActive ||
                    !selectedEpoch ||
                    hasExistingRewardsForSelectedEpoch
                  }
                >
                  {isStoring ? "Storing..." : "Store Staking Epoch Rewards"}
                </Button>
              </div>
            </div>
          )}

          <ConfirmationModal
            primaryColor={COLORS.orange.one}
            title="Overwrite Existing Rewards?"
            show={showConfirmModal}
            onHide={() => {
              setShowConfirmModal(false);
              setConfirmedOverwrite(false);
            }}
            extras={
              <div style={{ marginBottom: SPACING.three }}>
                <div style={FONT_STYLE.label.one}>
                  Rewards are already allocated for epoch {selectedEpoch}. Are
                  you sure you want to overwrite them?
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: SPACING.two,
                    marginTop: SPACING.three,
                  }}
                >
                  <Button
                    buttonTheme={
                      confirmedOverwrite
                        ? ButtonThemeEnum.POSITIVE
                        : ButtonThemeEnum.NEUTRAL2
                    }
                    onClick={() => setConfirmedOverwrite(true)}
                    style={{ flex: 1 }}
                  >
                    Yes
                  </Button>
                  <Button
                    buttonTheme={ButtonThemeEnum.NEUTRAL2}
                    onClick={() => {
                      setShowConfirmModal(false);
                      setConfirmedOverwrite(false);
                    }}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            }
            confirmationButton={{
              title: "Store Epoch Rewards",
              onClick: handleStoreEpochStake,
              disabled: !confirmedOverwrite || isStoring,
            }}
          />

          <TableWrapper>
            {isLoading ? <Spinner /> : null}
            <table {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  // eslint-disable-next-line react/jsx-key
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column: any) => (
                      // eslint-disable-next-line react/jsx-key
                      <TableHeaderCell
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ width: column.width }}
                      >
                        {column.render("Header")}
                      </TableHeaderCell>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  const { ...rowProps } = row.getRowProps();
                  return (
                    // eslint-disable-next-line react/jsx-key
                    <DashboardRow
                      {...rowProps}
                      onClick={() => {
                        const checksummedAddress = getChecksumAddress(
                          row.original.accountAddress
                        );
                        navigate(
                          `${PageEndpointEnum.ACCOUNT}/${checksummedAddress}`
                        );
                      }}
                      hasRecord={!!getAirtableTag(row.original.accountAddress)}
                    >
                      {row.cells.map((cell) => (
                        // eslint-disable-next-line react/jsx-key
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </DashboardRow>
                  );
                })}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ textAlign: "center" }}
                    >
                      {selectedEpoch
                        ? `No stakers found for epoch ${selectedEpoch}.`
                        : "Please select an epoch to view staking data."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableWrapper>
        </>
      )}

      {activeTab === StakeTabEnum.TRADING_REWARDS_INFO && (
        <TradingRewardsInfo />
      )}

      {activeTab === StakeTabEnum.MEGA_REWARDS && <MegaRewards />}

      {activeTab === StakeTabEnum.BUYBACKS_AND_BURNS && <BuybacksAndBurns />}
    </div>
  );
}
